import axios from 'axios';

type PaymentApiResponse = {
  checkout_status?: string;
  error?: boolean | string;
  message?: string;
  state?: string;
  status?: string;
  url?: string;
};

type ResolveInvoicePaymentOutcomeParams = {
  invoiceId: string;
  responseData?: PaymentApiResponse;
};

type ResolveInvoicePaymentOutcomeResult =
  | {
      kind: 'redirect';
      url: string;
    }
  | {
      kind: 'success';
    }
  | {
      kind: 'error';
      message: string;
    };

const SUCCESS_STATES = new Set([
  'paid',
  'payment_pending',
  'processing',
  'submitted',
  'success',
  'succeeded',
]);

const FAILURE_STATES = new Set([
  'canceled',
  'cancelled',
  'declined',
  'failed',
  'open',
  'overdue',
  'voided',
]);

const DEFAULT_FAILURE_MESSAGE =
  'Payment failed. Please try again or use a different payment method.';

const normalizeState = (value?: string) => value?.trim().toLowerCase();

const getMessageFromResponse = (responseData?: PaymentApiResponse) => {
  if (
    typeof responseData?.message === 'string' &&
    responseData.message.trim()
  ) {
    return responseData.message.trim();
  }

  if (typeof responseData?.error === 'string' && responseData.error.trim()) {
    return responseData.error.trim();
  }

  return undefined;
};

const isFailureResponse = (responseData?: PaymentApiResponse) => {
  const normalizedStatus = normalizeState(responseData?.status);
  const normalizedStateValue = normalizeState(responseData?.state);
  const normalizedCheckoutStatus = normalizeState(
    responseData?.checkout_status
  );

  return (
    responseData?.error === true ||
    FAILURE_STATES.has(normalizedStatus || '') ||
    FAILURE_STATES.has(normalizedStateValue || '') ||
    FAILURE_STATES.has(normalizedCheckoutStatus || '')
  );
};

const isSuccessResponse = (responseData?: PaymentApiResponse) => {
  const normalizedStatus = normalizeState(responseData?.status);
  const normalizedStateValue = normalizeState(responseData?.state);
  const normalizedCheckoutStatus = normalizeState(
    responseData?.checkout_status
  );

  return (
    SUCCESS_STATES.has(normalizedStatus || '') ||
    SUCCESS_STATES.has(normalizedStateValue || '') ||
    SUCCESS_STATES.has(normalizedCheckoutStatus || '')
  );
};

const fetchInvoiceStatus = async (invoiceId: string) => {
  const { data } = await axios.get<{ state?: string }>(
    `${process.env.REACT_APP_BILLING_SERVICE}/payment/invoice-status`,
    {
      params: { invoice_id: invoiceId },
    }
  );

  return normalizeState(data?.state);
};

export const resolveInvoicePaymentOutcome = async ({
  invoiceId,
  responseData,
}: ResolveInvoicePaymentOutcomeParams): Promise<ResolveInvoicePaymentOutcomeResult> => {
  if (responseData?.url) {
    return {
      kind: 'redirect',
      url: responseData.url,
    };
  }

  if (isFailureResponse(responseData)) {
    return {
      kind: 'error',
      message: getMessageFromResponse(responseData) || DEFAULT_FAILURE_MESSAGE,
    };
  }

  try {
    const invoiceStatus = await fetchInvoiceStatus(invoiceId);

    if (SUCCESS_STATES.has(invoiceStatus || '')) {
      return { kind: 'success' };
    }

    if (FAILURE_STATES.has(invoiceStatus || '')) {
      return {
        kind: 'error',
        message:
          getMessageFromResponse(responseData) || DEFAULT_FAILURE_MESSAGE,
      };
    }
  } catch (_error) {
    if (isSuccessResponse(responseData)) {
      return { kind: 'success' };
    }
  }

  if (isSuccessResponse(responseData)) {
    return { kind: 'success' };
  }

  return { kind: 'success' };
};

export { DEFAULT_FAILURE_MESSAGE };
